import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../api";
import { useApp } from "../context/AppContext";
import { Seo } from "../components/Seo";
import { VerificationBadge } from "../components/StatusPill";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const FIELDS = [
  ["name", "Your Name", true], ["email", "Email", true], ["phone", "Phone", false],
  ["property_address", "Property Address", false], ["district", "District", false],
  ["taluka", "Taluka", false], ["village", "Village", false],
  ["survey_number", "Survey Number", false], ["block_number", "Block Number", false],
  ["tp_number", "TP Number", false], ["fp_number", "FP Number", false],
  ["property_card_number", "Property Card Number", false], ["document_number", "Document Number", false],
  ["owner_name", "Owner Name", false],
];

const CHECK_LABELS = {
  ok: ["bg-green-500", "Found / no issue"], pending: ["bg-amber-400", "Needs verification"], fail: ["bg-red-500", "Issue / insufficient data"],
};

export default function Verification() {
  const { t } = useApp();
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trackId, setTrackId] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/verification/submit", form);
      setResult(data);
      toast.success(`Verification request ${data.tracking_id} submitted`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const track = async () => {
    try {
      const { data } = await api.get(`/verification/track/${trackId}`);
      setResult(data);
    } catch {
      toast.error("Tracking ID not found");
    }
  };

  const input = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="verification-page">
      <Seo title="Property Verification Gujarat" description="Verify ownership, transaction history, encumbrance and ferfar records for any Gujarat property. GREEN/YELLOW/RED report." />
      <h1 className="font-display font-bold text-3xl md:text-4xl flex items-center gap-3">
        <ShieldCheck className="text-secondary" size={34} /> {t("verification")}
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Submit property identifiers and get a checklist-based verification report.
        Our team reviews every request; the preliminary result below is instant.
      </p>

      <div className="mt-6 bg-white border border-border rounded-xl p-5 flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs font-semibold text-muted-foreground">Track existing request</label>
          <input className={input} placeholder="VR..." value={trackId} onChange={(e) => setTrackId(e.target.value)} data-testid="verification-track-input" />
        </div>
        <Button onClick={track} variant="outline" data-testid="verification-track-btn">{t("track")}</Button>
      </div>

      <form onSubmit={submit} className="mt-6 bg-white border border-border rounded-xl p-6" data-testid="verification-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(([key, label, req]) => (
            <div key={key}>
              <label className="text-xs font-semibold text-muted-foreground" htmlFor={`vf-${key}`}>{label}{req && " *"}</label>
              <input id={`vf-${key}`} className={input} required={req} value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`vf-${key}`} />
            </div>
          ))}
        </div>
        <Button type="submit" className="mt-5 bg-primary hover:bg-primary/90 h-11 px-8" disabled={loading} data-testid="verification-submit-btn">
          {loading ? "Submitting..." : t("verify_property")}
        </Button>
      </form>

      {result && (
        <div className="mt-8 bg-white border border-border rounded-xl p-6 fade-up" data-testid="verification-result">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-muted-foreground">Tracking ID</p>
              <p className="font-mono font-bold text-lg" data-testid="verification-tracking-id">{result.tracking_id}</p>
            </div>
            <VerificationBadge level={result.overall} />
          </div>
          <p className="mt-3 text-sm">{result.note}</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-2">
            {(result.checklist || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-sm border border-border rounded-lg px-3 py-2" data-testid={`checklist-item-${i}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${CHECK_LABELS[c.status]?.[0] || "bg-slate-300"}`} />
                <span className="flex-1">{c.item}</span>
                <span className="text-xs text-muted-foreground">{CHECK_LABELS[c.status]?.[1] || c.status}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="verification-disclaimer">
            {t("verification_disclaimer")}
          </p>
        </div>
      )}
    </div>
  );
}
